import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { EyeIcon, EyeSlashIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import { portalApi } from "../services/portalApi";

const inputCls =
  "w-full rounded-xl border border-[#d4cceb] bg-white px-4 py-3 text-[0.9375rem] text-brand-dark placeholder:text-brand-gray/50 focus:border-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-navy/15 transition-colors";

// ── OTP input: 6 individual digit boxes ──────────────────────────────────────
function OtpInput({ value, onChange, disabled }) {
  const digits = (value + "      ").slice(0, 6).split("");
  const refs = useRef([]);

  function handleKey(i, e) {
    if (e.key === "Backspace") {
      if (digits[i] !== " " && digits[i] !== "") {
        const next = digits.map((d, j) => (j === i ? "" : d)).join("").trimEnd();
        onChange(next.padEnd(6, " ").slice(0, 6).replace(/ /g, ""));
      } else if (i > 0) {
        refs.current[i - 1]?.focus();
        const next = digits
          .map((d, j) => (j === i - 1 ? "" : d))
          .join("")
          .trimEnd();
        onChange(next.padEnd(6, " ").slice(0, 6).replace(/ /g, ""));
      }
      e.preventDefault();
    } else if (e.key === "ArrowLeft" && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === "ArrowRight" && i < 5) {
      refs.current[i + 1]?.focus();
    }
  }

  function handleInput(i, e) {
    const char = e.target.value.replace(/\D/g, "").slice(-1);
    if (!char) return;
    const next = digits.map((d, j) => (j === i ? char : d === " " ? "" : d)).join("");
    onChange(next.replace(/ /g, "").slice(0, 6));
    if (i < 5) refs.current[i + 1]?.focus();
  }

  function handlePaste(e) {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    onChange(pasted);
    refs.current[Math.min(pasted.length, 5)]?.focus();
    e.preventDefault();
  }

  const clean = value.replace(/\D/g, "").padEnd(6, "").slice(0, 6);

  return (
    <div className="flex gap-3 justify-center" onPaste={handlePaste}>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={clean[i] || ""}
          onChange={(e) => handleInput(i, e)}
          onKeyDown={(e) => handleKey(i, e)}
          disabled={disabled}
          className={[
            "w-12 h-14 rounded-xl border text-center text-xl font-semibold text-brand-dark",
            "focus:outline-none focus:ring-2 focus:ring-brand-navy/20 transition-colors",
            clean[i]
              ? "border-brand-navy bg-brand-beige"
              : "border-[#d4cceb] bg-white",
            disabled ? "opacity-50 cursor-not-allowed" : "",
          ].join(" ")}
        />
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function PortalForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=email, 2=code+password, 3=success
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  // Step 1 — send code
  async function onSendCode(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await portalApi.forgotPassword({ email: email.trim() });
      setStep(2);
      toast.success("Código enviado. Revisa tu bandeja.");
    } catch (err) {
      toast.error(err.message || "No se pudo enviar el código");
    } finally {
      setLoading(false);
    }
  }

  // Step 2 — verify code + set new password
  async function onResetPassword(e) {
    e.preventDefault();
    if (code.replace(/\D/g, "").length < 6) {
      toast.error("Ingresa el código de 6 dígitos completo");
      return;
    }
    if (password !== passwordConfirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    if (password.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    setLoading(true);
    try {
      await portalApi.resetPassword({
        email: email.trim(),
        code: code.replace(/\D/g, ""),
        password,
        passwordConfirm,
      });
      setStep(3);
    } catch (err) {
      toast.error(err.message || "No se pudo restablecer la contraseña");
    } finally {
      setLoading(false);
    }
  }

  // Resend code
  async function onResend() {
    setLoading(true);
    setCode("");
    try {
      await portalApi.forgotPassword({ email: email.trim() });
      toast.success("Nuevo código enviado");
    } catch (err) {
      toast.error(err.message || "No se pudo reenviar el código");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen">
      {/* ── Left — form ── */}
      <div className="flex w-full flex-col items-center bg-white min-h-screen lg:w-1/2">
        <div className="w-full max-w-md px-8 py-16 my-auto">

          {/* ── Step 1: email ── */}
          {step === 1 && (
            <>
              <div className="mb-10">
                <h1 className="font-sans text-[2rem] font-semibold tracking-tight text-brand-dark">
                  Recuperar contraseña
                </h1>
                <p className="mt-2 text-[0.9375rem] text-brand-gray leading-snug">
                  Ingresa tu correo y te enviaremos un código de 6 dígitos para
                  crear una nueva contraseña.
                </p>
              </div>

              <form onSubmit={onSendCode} className="space-y-5" noValidate>
                <div className="space-y-1.5">
                  <label htmlFor="email" className="portal-label">
                    Correo electrónico
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="tu@correo.com"
                    className={inputCls}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="w-full rounded-xl bg-brand-navy py-3 text-[1rem] font-semibold text-white shadow-md shadow-brand-navy/20 hover:brightness-110 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Enviando…" : "Enviar código"}
                </button>
              </form>

              <p className="mt-8 text-center text-[0.9375rem] text-brand-gray">
                <Link to="/login" className="font-semibold text-brand-navy hover:underline">
                  Volver a iniciar sesión
                </Link>
              </p>
            </>
          )}

          {/* ── Step 2: code + new password ── */}
          {step === 2 && (
            <>
              <button
                onClick={() => { setStep(1); setCode(""); }}
                className="mb-8 flex items-center gap-1.5 text-sm text-brand-gray hover:text-brand-dark transition-colors"
              >
                <ArrowLeftIcon className="h-4 w-4" aria-hidden />
                Cambiar correo
              </button>

              <div className="mb-8">
                <h1 className="font-sans text-[2rem] font-semibold tracking-tight text-brand-dark">
                  Ingresa tu código
                </h1>
                <p className="mt-2 text-[0.9375rem] text-brand-gray leading-snug">
                  Enviamos un código de 6 dígitos a{" "}
                  <span className="font-semibold text-brand-dark">{email.trim()}</span>.
                  Válido por 30 minutos.
                </p>
              </div>

              <form onSubmit={onResetPassword} className="space-y-6" noValidate>
                {/* OTP */}
                <div className="space-y-3">
                  <label className="portal-label block text-center">Código de verificación</label>
                  <OtpInput value={code} onChange={setCode} disabled={loading} />
                  <p className="text-center text-xs text-brand-gray">
                    ¿No llegó?{" "}
                    <button
                      type="button"
                      onClick={onResend}
                      disabled={loading}
                      className="font-semibold text-brand-navy hover:underline disabled:opacity-50"
                    >
                      Reenviar código
                    </button>
                  </p>
                </div>

                <hr className="border-[#e8e2f5]" />

                {/* New password */}
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label htmlFor="password" className="portal-label">
                      Contraseña nueva{" "}
                      <span className="font-normal text-brand-gray">(mín. 8 caracteres)</span>
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={8}
                        autoComplete="new-password"
                        placeholder="••••••••"
                        className={`${inputCls} pr-12`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-brand-gray hover:text-brand-dark transition-colors"
                        aria-label={showPassword ? "Ocultar" : "Mostrar"}
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-5 w-5" aria-hidden />
                        ) : (
                          <EyeIcon className="h-5 w-5" aria-hidden />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label htmlFor="passwordConfirm" className="portal-label">
                      Confirmar contraseña nueva
                    </label>
                    <div className="relative">
                      <input
                        id="passwordConfirm"
                        type={showConfirm ? "text" : "password"}
                        value={passwordConfirm}
                        onChange={(e) => setPasswordConfirm(e.target.value)}
                        required
                        autoComplete="new-password"
                        placeholder="••••••••"
                        className={`${inputCls} pr-12`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirm((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-brand-gray hover:text-brand-dark transition-colors"
                        aria-label={showConfirm ? "Ocultar" : "Mostrar"}
                      >
                        {showConfirm ? (
                          <EyeSlashIcon className="h-5 w-5" aria-hidden />
                        ) : (
                          <EyeIcon className="h-5 w-5" aria-hidden />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || code.replace(/\D/g, "").length < 6 || !password}
                  className="w-full rounded-xl bg-brand-navy py-3 text-[1rem] font-semibold text-white shadow-md shadow-brand-navy/20 hover:brightness-110 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Guardando…" : "Crear nueva contraseña"}
                </button>
              </form>
            </>
          )}

          {/* ── Step 3: success ── */}
          {step === 3 && (
            <div className="text-center">
              {/* Checkmark circle */}
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-brand-beige">
                <svg viewBox="0 0 24 24" className="h-10 w-10 text-brand-navy" fill="none" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </div>
              <h1 className="font-sans text-[2rem] font-semibold tracking-tight text-brand-dark">
                ¡Contraseña actualizada!
              </h1>
              <p className="mt-3 text-[0.9375rem] text-brand-gray leading-relaxed">
                Tu contraseña se cambió correctamente. Ya puedes iniciar sesión
                con tus nuevas credenciales.
              </p>
              <button
                onClick={() => navigate("/login", { replace: true })}
                className="mt-8 w-full rounded-xl bg-brand-navy py-3 text-[1rem] font-semibold text-white shadow-md shadow-brand-navy/20 hover:brightness-110 transition-all"
              >
                Ir a iniciar sesión
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Right — brand panel ── */}
      <div
        className="hidden lg:flex flex-col items-center justify-center bg-brand-navy px-12 py-16 fixed right-0 top-0 w-1/2 h-screen"
        aria-hidden
      >
        <img
          src="/images/Logo-v2.svg"
          alt=""
          className="w-full max-w-[26rem] brightness-0 invert"
          draggable={false}
        />
        <p className="mt-8 text-[0.9375rem] font-light tracking-widest text-white/60 uppercase">
          Programa de Asistencia al Empleado
        </p>
      </div>
    </div>
  );
}
